import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AccountAssignmentGroup = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('account_assignment_groups');
  const [viewItem, setViewItem] = useState(null);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/account-assignment-groups`);
      setGroups(response.data || []);
    } catch (error) {
      console.error('Error loading account assignment groups:', error);
      toast.error('Failed to load account assignment groups');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (group = null) => {
    setEditingGroup(group);
    setIsDialogOpen(true);
  };

  const normalizeGroupId = (id) => {
    // Normalize the group ID to prevent duplicates
    // If purely numeric, remove leading zeros
    const trimmed = id.trim().toUpperCase();
    if (/^\d+$/.test(trimmed)) {
      return String(parseInt(trimmed, 10));
    }
    return trimmed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const rawId = formData.get('account_assignment_group_id') || '';
    const normalizedId = normalizeGroupId(rawId);
    
    // Validate: must be 1-2 characters after normalization
    if (!normalizedId || normalizedId.length > 2) {
      toast.error('Group ID must be 1-2 characters');
      return;
    }

    const data = {
      account_assignment_group_id: normalizedId,
      description: formData.get('description')
    };

    try {
      if (editingGroup) {
        await axios.put(`${BACKEND_URL}/api/account-assignment-groups/${editingGroup.account_assignment_group_id}`, {
          description: data.description
        });
        toast.success('Account Assignment Group updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/account-assignment-groups`, data);
        toast.success('Account Assignment Group created successfully');
      }
      setIsDialogOpen(false);
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save account assignment group');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Account Assignment Groups</h1>
            <p className="text-sm text-gray-600 mt-1">Manage account assignment groups for material master</p>
          </div>
          {writable && (<Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Account Assignment Group
          </Button>)}
        </div>

        <Card className="p-6">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : groups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No account assignment groups found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Group ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => (
                    <tr key={group.account_assignment_group_id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono">{group.account_assignment_group_id}</td>
                      <td className="py-3 px-4">{group.description}</td>
                      <td className="py-3 px-4 text-right">
                        <RecordActions doctype="account_assignment_groups" onView={() => setViewItem(group)} onEdit={() => handleOpenDialog(group)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingGroup ? 'Edit Account Assignment Group' : 'Create Account Assignment Group'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="account_assignment_group_id">Group ID * (max 2 chars)</Label>
                <Input
                  id="account_assignment_group_id"
                  name="account_assignment_group_id"
                  defaultValue={editingGroup?.account_assignment_group_id || ''}
                  placeholder="e.g., 1, 2, A1, GC"
                  maxLength={2}
                  disabled={!!editingGroup}
                  className="uppercase"
                  style={{ textTransform: 'uppercase' }}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Alphanumeric, max 2 characters. Note: Leading zeros are removed (e.g., "01" becomes "1")
                </p>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingGroup?.description || ''}
                  placeholder="e.g., Trading goods, Services"
                  maxLength={100}
                  required
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingGroup ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default AccountAssignmentGroup;
